/**
 * nearby.js
 * http://www.codrops.com
 *
 * Licensed under the MIT license.
 * http://www.opensource.org/licenses/mit-license.php
 *
 * Copyright 2018, Codrops
 * http://www.codrops.com
 */
import { useEffect } from 'react';

const distanceBetweenPoints = (x1: number, y1: number, x2: number, y2: number) => Math.sqrt((x1 - x2) * (x1 - x2) + (y1 - y2) * (y1 - y2));

// from http://www.quirksmode.org/js/events_properties.html#position
const getMousePos = (e: MouseEvent) => {
  let posx = 0;
  let posy = 0;
  if (e.pageX || e.pageY) {
    posx = e.pageX;
    posy = e.pageY;
  } else if (e.clientX || e.clientY) {
    posx = e.clientX + document.body.scrollLeft + document.documentElement.scrollLeft;
    posy = e.clientY + document.body.scrollTop + document.documentElement.scrollTop;
  }
  return { x: posx, y: posy };
};

export const useNearby = (elementRef: React.RefObject<HTMLElement>, onProgress: (distance: number) => void) => {
  useEffect(() => {
    const onWindowMouseMove = (ev: MouseEvent) => {
      requestAnimationFrame(() => {
        if (elementRef.current) {
          const mousePosition = getMousePos(ev);
          const docScrolls = {
            left: document.body.scrollLeft + document.documentElement.scrollLeft,
            top: document.body.scrollTop + document.documentElement.scrollTop,
          };
          const elRect = elementRef.current.getBoundingClientRect();
          const elCoords = {
            x1: elRect.left + docScrolls.left,
            x2: elRect.width + elRect.left + docScrolls.left,
            y1: elRect.top + docScrolls.top,
            y2: elRect.height + elRect.top + docScrolls.top,
          };
          const closestPoint = { x: mousePosition.x, y: mousePosition.y };

          if (mousePosition.x < elCoords.x1) {
            closestPoint.x = elCoords.x1;
          } else if (mousePosition.x > elCoords.x2) {
            closestPoint.x = elCoords.x2;
          }
          if (mousePosition.y < elCoords.y1) {
            closestPoint.y = elCoords.y1;
          } else if (mousePosition.y > elCoords.y2) {
            closestPoint.y = elCoords.y2;
          }
          if (onProgress) {
            onProgress(distanceBetweenPoints(mousePosition.x, mousePosition.y, closestPoint.x, closestPoint.y));
          }
        }
      });
    };

    window.addEventListener('mousemove', onWindowMouseMove);
    return () => {
      window.removeEventListener('mousemove', onWindowMouseMove);
    };
  }, [elementRef, onProgress]);
};
